import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, Building2, Phone, Mail, Globe, CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DashboardLayout from '../components/Layout/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EinstellungenPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    firma_name: '', firma_inhaber: '', firma_adresse: '', firma_plz_ort: '',
    firma_telefon: '', firma_mobil: '', firma_email: '', firma_web: '',
    firma_iban: '', firma_bic: '', firma_steuernr: '', firma_handelsregister: '', firma_amtsgericht: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/'); return; }
    setUser(JSON.parse(userData));

    fetch(`${API_URL}/api/settings`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {})
      .then(data => setSettings(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, [navigate]);

  const handleSave = async () => {
    setIsSaving(true); setSaved(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) setSaved(true);
    } catch {} finally { setIsSaving(false); }
  };

  const Field = ({ label, field, placeholder, type = 'text', icon: Icon }) => (
    <div>
      <Label className="text-gray-700 mb-2 block text-sm">{label}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
        <Input
          type={type}
          value={settings[field]}
          onChange={(e) => { setSettings(p => ({ ...p, [field]: e.target.value })); setSaved(false); }}
          placeholder={placeholder}
          className={`h-10 border-gray-200 rounded-none ${Icon ? 'pl-10' : ''} ${settings[field] ? 'bg-green-50 border-green-300' : ''}`}
        />
      </div>
    </div>
  );

  if (!user) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <DashboardLayout user={user}>
      <div className="p-4 lg:p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-gray-400" />
            <h1 className="text-xl font-bold text-gray-900">Einstellungen</h1>
          </div>
          <Button onClick={handleSave} disabled={isSaving} className="bg-[#29beff] hover:bg-[#1fb3f5] text-white gap-2 h-9">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {isSaving ? 'Speichert...' : saved ? 'Gespeichert' : 'Speichern'}
          </Button>
        </div>

        {/* Firmendaten */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-400" /> Firmendaten
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Firmenname" field="firma_name" placeholder="z.B. Autogutachter24" icon={Building2} />
            <Field label="Inhaber / Gutachter" field="firma_inhaber" placeholder="z.B. Max Mustermann" />
            <Field label="Adresse" field="firma_adresse" placeholder="Straße und Hausnummer" />
            <Field label="PLZ / Ort" field="firma_plz_ort" placeholder="z.B. 50829 Köln" />
          </div>
        </div>

        {/* Kontakt */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-gray-400" /> Kontaktdaten
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Telefon" field="firma_telefon" placeholder="Festnetz" icon={Phone} />
            <Field label="Mobil" field="firma_mobil" placeholder="Mobilnummer" icon={Phone} />
            <Field label="E-Mail" field="firma_email" placeholder="email@beispiel.de" type="email" icon={Mail} />
            <Field label="Webseite" field="firma_web" placeholder="www.beispiel.de" icon={Globe} />
          </div>
        </div>

        {/* Bankdaten */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-400" /> Bank- & Steuerdaten
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="IBAN" field="firma_iban" placeholder="DE00 0000 0000 0000 0000 00" icon={CreditCard} />
            <Field label="BIC" field="firma_bic" placeholder="XXXXXXXX" />
            <Field label="Steuernummer" field="firma_steuernr" placeholder="000/0000/0000" />
            <Field label="Handelsregister-Nr." field="firma_handelsregister" placeholder="HRB 00000" />
            <Field label="Amtsgericht" field="firma_amtsgericht" placeholder="z.B. Amtsgericht Köln" />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EinstellungenPage;
